import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/Navbar';
import { StoreIdCard } from '../components/StoreIdCard';
import { Package, Tag, TrendingUp, Zap, AlertTriangle, ArrowUp, ShoppingCart, MessageSquare, DollarSign, FileText, Video, CreditCard, Ticket, Sparkles, FolderOpen, Calendar, Palette, UserCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const SellerDashboardNew = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [creditStatus, setCreditStatus] = useState(null);
  const [plans, setPlans] = useState({});
  const [creditPacks, setCreditPacks] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('products');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [profileComplete, setProfileComplete] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, couponsRes, subRes, statusRes, plansRes, packsRes, profileRes] = await Promise.all([
        axios.get(`${API_URL}/seller/products`, { withCredentials: true }),
        axios.get(`${API_URL}/seller/coupons`, { withCredentials: true }),
        axios.get(`${API_URL}/saas/subscription`, { withCredentials: true }),
        axios.get(`${API_URL}/saas/credits/status`, { withCredentials: true }),
        axios.get(`${API_URL}/saas/plans`),
        axios.get(`${API_URL}/saas/credit-packs`),
        axios.get(`${API_URL}/seller/profile`, { withCredentials: true }).catch(() => null)
      ]);
      
      setProducts(productsRes.data);
      setCoupons(couponsRes.data);
      setSubscription(subRes.data);
      setCreditStatus(statusRes.data);
      setPlans(plansRes.data.plans);
      setCreditPacks(packsRes.data.packs);
      setProfileComplete(profileRes?.data?.profile_complete || false);

      // Mostrar aviso se estiver próximo do limite
      if (statusRes.data.status === 'warning') {
        toast.warning(statusRes.data.message);
      } else if (statusRes.data.status === 'limit_reached') {
        toast.error(statusRes.data.message);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (newPlan) => {
    try {
      await axios.post(`${API_URL}/saas/upgrade`, { new_plan: newPlan }, { withCredentials: true });
      toast.success('Plano atualizado com sucesso!');
      setShowUpgradeModal(false);
      fetchData();
    } catch (error) {
      toast.error('Erro ao fazer upgrade');
    }
  };

  const handleBuyCredits = (packType) => {
    // Novo fluxo: Checkout Transparente unificado.
    setShowCreditsModal(false);
    navigate(`/checkout?target=credit_pack&target_id=${packType}`);
  };

  const creditsPercent = subscription 
    ? (subscription.credits_used / subscription.credits_total) * 100 
    : 0;

  const getProgressColor = () => {
    if (creditsPercent >= 100) return 'bg-red-600';
    if (creditsPercent >= 80) return 'bg-amber-600';
    return 'bg-green-600';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="seller-dashboard-title">
            Painel do Vendedor
          </h1>
          <p className="text-lg text-gray-600">Bem-vindo, {user?.name}</p>
        </div>

        {/* Barra de Créditos */}
        {subscription && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <Zap className="w-6 h-6 text-indigo-600" />
                <div>
                  <h3 className="font-semibold text-gray-900">Créditos Disponíveis</h3>
                  <p className="text-sm text-gray-600">
                    Plano: <span className="font-medium text-indigo-600">{plans[subscription.plan]?.name}</span>
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-gray-900">
                  {subscription.credits_remaining}
                </p>
                <p className="text-sm text-gray-600">de {subscription.credits_total} créditos</p>
              </div>
            </div>

            {/* Barra de Progresso */}
            <div className="relative w-full h-4 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full ${getProgressColor()} transition-all duration-500`}
                style={{ width: `${Math.min(creditsPercent, 100)}%` }}
              />
            </div>

            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-600">
                {creditsPercent.toFixed(1)}% usado
              </p>
              <div className="flex space-x-2">
                <Button
                  onClick={() => setShowCreditsModal(true)}
                  size="sm"
                  variant="outline"
                  data-testid="buy-credits-btn"
                >
                  <ShoppingCart className="w-4 h-4 mr-1" />
                  Comprar Créditos
                </Button>
                <Button
                  onClick={() => navigate('/seller/planos')}
                  size="sm"
                  className="bg-gradient-to-r from-indigo-600 to-purple-600"
                  data-testid="upgrade-plan-btn"
                >
                  <CreditCard className="w-4 h-4 mr-1" />
                  Ver Planos
                </Button>
              </div>
            </div>

            {/* Avisos */}
            {creditStatus?.status === 'warning' && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start space-x-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">{creditStatus.message}</p>
              </div>
            )}

            {creditStatus?.status === 'limit_reached' && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{creditStatus.message}</p>
              </div>
            )}

            <p className="text-xs text-gray-500 mt-3">
              Renovação automática em: {formatDate(subscription.expires_at)}
            </p>
          </div>
        )}

        {/* Banner Perfil Incompleto */}
        {profileComplete === false && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-4" data-testid="dashboard-profile-alert">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-800">Complete seu cadastro</p>
              <p className="text-xs text-amber-600 mt-0.5">Preencha seus dados para desbloquear a assinatura de planos e todas as funcionalidades.</p>
            </div>
            <Button
              size="sm"
              onClick={() => navigate('/seller/complete-profile')}
              className="bg-amber-500 hover:bg-amber-600 text-white whitespace-nowrap"
              data-testid="dashboard-complete-profile-btn"
            >
              Completar agora
            </Button>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-8">
          <Button
            onClick={() => navigate('/seller/products')}
            className="h-auto py-4 bg-indigo-600 hover:bg-indigo-700 flex flex-col items-center gap-2"
            data-testid="my-products-btn"
          >
            <Package className="w-6 h-6" />
            <span>Meus Produtos</span>
            <span className="text-xs opacity-80">Adicionar e gerenciar</span>
          </Button>
          <Button
            onClick={() => navigate('/seller/categories')}
            className="h-auto py-4 bg-violet-600 hover:bg-violet-700 flex flex-col items-center gap-2"
            data-testid="my-categories-btn"
          >
            <FolderOpen className="w-6 h-6" />
            <span>Categorias</span>
            <span className="text-xs opacity-80">Organizar loja</span>
          </Button>
          <Button
            onClick={() => navigate('/seller/pedidos')}
            className="h-auto py-4 bg-orange-600 hover:bg-orange-700 flex flex-col items-center gap-2"
            data-testid="orders-btn"
          >
            <ShoppingCart className="w-6 h-6" />
            <span>Pedidos</span>
            <span className="text-xs opacity-80">Gerenciar vendas</span>
          </Button>
          <Button
            onClick={() => navigate('/seller/mercadopago')}
            className="h-auto py-4 bg-cyan-600 hover:bg-cyan-700 flex flex-col items-center gap-2"
            data-testid="mp-config-btn"
          >
            <CreditCard className="w-6 h-6" />
            <span>Mercado Pago</span>
            <span className="text-xs opacity-80">Receber pagamentos</span>
          </Button>
          <Button
            onClick={() => navigate('/seller/whatsapp')}
            className="h-auto py-4 bg-green-600 hover:bg-green-700 flex flex-col items-center gap-2"
            data-testid="whatsapp-config-btn"
          >
            <MessageSquare className="w-6 h-6" />
            <span>WhatsApp IA</span>
            <span className="text-xs opacity-80">Automação</span>
          </Button>
          <Button
            onClick={() => navigate('/seller/financeiro')}
            className="h-auto py-4 bg-emerald-600 hover:bg-emerald-700 flex flex-col items-center gap-2"
            data-testid="financial-btn"
          >
            <DollarSign className="w-6 h-6" />
            <span>Financeiro</span>
            <span className="text-xs opacity-80">Receitas + Recorrências</span>
          </Button>
          <Button
            onClick={() => navigate('/seller/paginas')}
            className="h-auto py-4 bg-purple-600 hover:bg-purple-700 flex flex-col items-center gap-2"
            data-testid="pages-btn"
          >
            <FileText className="w-6 h-6" />
            <span>Páginas de Venda</span>
            <span className="text-xs opacity-80">Landing pages</span>
          </Button>
          <Button
            onClick={() => navigate('/seller/vitrine')}
            className="h-auto py-4 bg-rose-600 hover:bg-rose-700 flex flex-col items-center gap-2"
            data-testid="customize-store-btn"
          >
            <Palette className="w-6 h-6" />
            <span>Personalizar</span>
            <span className="text-xs opacity-80">Vitrine</span>
          </Button>
          <Button
            onClick={() => navigate('/seller/complete-profile')}
            className="h-auto py-4 bg-teal-600 hover:bg-teal-700 flex flex-col items-center gap-2"
            data-testid="complete-profile-btn"
          >
            <UserCheck className="w-6 h-6" />
            <span>Meu Cadastro</span>
            <span className="text-xs opacity-80">Dados completos</span>
          </Button>
          <Button
            onClick={() => navigate('/seller/cupons')}
            className="h-auto py-4 bg-pink-600 hover:bg-pink-700 flex flex-col items-center gap-2"
            data-testid="coupons-btn"
          >
            <Ticket className="w-6 h-6" />
            <span>Cupons</span>
            <span className="text-xs opacity-80">Descontos</span>
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-12">
          {/* Store ID Card - Ocupa 1 coluna */}
          {user?.store_id && (
            <div className="lg:col-span-1">
              <StoreIdCard storeId={user.store_id} storeName={user.store_name || 'Needi'} />
            </div>
          )}

          {/* Stats - Ocupam 3 colunas */}
          <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Produtos</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{products.length}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Package className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Cupons</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{coupons.length}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Tag className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Status</p>
                <p className="text-lg font-bold text-green-600 mt-2">Ativo</p>
              </div>
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-indigo-600" />
              </div>
              </div>
            </div>
          </div>
        </div>

        {/* Conteúdo existente do dashboard */}
        {/* ... resto do código do SellerDashboard atual ... */}
      </div>

      {/* Modal de Upgrade */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Fazer Upgrade do Plano</DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {Object.entries(plans).map(([key, plan]) => {
              const isCurrent = subscription?.plan === key;
              const isLower = subscription && Object.keys(plans).indexOf(key) <= Object.keys(plans).indexOf(subscription.plan);
              
              return (
                <div
                  key={key}
                  className={`p-6 rounded-xl border-2 transition-all ${
                    isCurrent ? 'border-indigo-600 bg-indigo-50' :
                    isLower ? 'border-gray-200 bg-gray-50 opacity-50' :
                    'border-gray-200 hover:border-indigo-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                    {plan.popular && (
                      <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">
                        POPULAR
                      </span>
                    )}
                  </div>
                  
                  <p className="text-3xl font-bold text-indigo-600 mb-2">
                    R$ {plan.price.toFixed(2)}
                    <span className="text-sm font-normal text-gray-600">/mês</span>
                  </p>
                  
                  <p className="text-sm text-gray-600 mb-4">
                    {plan.credits} créditos/mês
                  </p>
                  
                  <Button
                    onClick={() => handleUpgrade(key)}
                    disabled={isCurrent || isLower}
                    className="w-full"
                    variant={isCurrent ? "outline" : "default"}
                  >
                    {isCurrent ? 'Plano Atual' : isLower ? 'Indisponível' : 'Selecionar'}
                  </Button>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Compra de Créditos */}
      <Dialog open={showCreditsModal} onOpenChange={setShowCreditsModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Comprar Créditos Extras</DialogTitle>
          </DialogHeader>
          
          <p className="text-sm text-gray-600 mb-4">
            Continue vendendo sem interrupções! Os créditos expiram junto com seu ciclo mensal.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Object.entries(creditPacks).map(([key, pack]) => (
              <div
                key={key}
                className={`p-6 rounded-xl border-2 transition-all ${
                  pack.highlight 
                    ? 'border-indigo-600 bg-indigo-50' 
                    : 'border-gray-200 hover:border-indigo-300'
                }`}
              >
                {pack.highlight && (
                  <span className="inline-block px-3 py-1 bg-indigo-600 text-white text-xs font-bold rounded-full mb-3">
                    MELHOR OPÇÃO
                  </span>
                )}
                
                <p className="text-2xl font-bold text-gray-900 mb-2">
                  {pack.credits} créditos
                </p>
                
                <p className="text-3xl font-bold text-indigo-600 mb-4">
                  R$ {pack.price.toFixed(2)}
                </p>
                
                <Button
                  onClick={() => handleBuyCredits(key)}
                  className="w-full"
                  variant={pack.highlight ? "default" : "outline"}
                >
                  Comprar
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
